<script lang="ts">
  import { onMount } from 'svelte';
  import { authState, signIn } from '../lib/auth';
  import { hasCloud, db } from '../lib/db';
  import AnimatedTree from './AnimatedTree.svelte';

  type Mode = 'signin' | 'signup';
  let mode: Mode = 'signin';
  let email = '';
  let loading = false;
  let error = '';
  let info = '';
  let invitedAuto = false;

  async function submit(opts: { autoApprove?: boolean } = {}) {
    error = ''; info = '';
    loading = true;
    try {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
        throw new Error('Enter a valid email address.');
      }
      if (mode === 'signin' && !opts.autoApprove) {
        // Existing-user check: profile must already exist locally OR cloud login
        // will validate via OTP. In local-only mode, refuse if no profile exists.
        if (!hasCloud) {
          const existing = await db.profiles.where('email').equals(trimmed).first();
          if (!existing) {
            throw new Error("No account for that email on this device. Switch to “I'm new” to create one.");
          }
        }
      }
      await signIn(trimmed, { autoApprove: opts.autoApprove });
      if (mode === 'signup' && !opts.autoApprove) {
        info = 'Account created. Awaiting admin approval.';
      }
    } catch (e) {
      error = (e as Error).message;
    } finally {
      loading = false;
    }
  }

  function setMode(m: Mode) {
    mode = m; error = ''; info = '';
  }

  // Magic invite link: ?invite=<email> auto-fills + auto-signs the visitor
  // in. First visitor on a browser bootstraps as admin (local-only mode).
  // Used to send testers a one-click sign-in URL by email.
  onMount(async () => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    if (!invite) return;
    email = invite;
    invitedAuto = true;
    // Decide which mode: existing profile → sign in; otherwise → request access
    const existing = await db.profiles.where('email').equals(invite.toLowerCase()).first();
    mode = existing ? 'signin' : 'signup';
    // Small delay so the splash paints before we redirect.
    // autoApprove: true — the invite link IS the approval, no admin gate.
    setTimeout(() => submit({ autoApprove: true }), 350);
  });
</script>

{#if $authState.status === 'loading'}
  <div class="center muted mono">loading…</div>
{:else if $authState.status === 'anonymous'}
  <div class="splash">
    <div class="hero enter">
      <div class="meta">
        <span class="chip signal">v0.1 · preview</span>
        <span class="chip">local-first</span>
        <span class="chip">GEDCOM 5.5.1</span>
      </div>
      <h1>A family tree,<br/>built like a <em>tool</em>.</h1>
      <p class="lede">
        Fast. Private. Mergeable. Linsmart is a local-first family tree for
        people who'd rather work with their data than rent it.
      </p>
      <ul class="bullets">
        <li><span class="tick">◆</span> Up to 10,000 people. Pure CSS rendering — no graphics tax.</li>
        <li><span class="tick">◆</span> Living people never published without explicit consent.</li>
        <li><span class="tick">◆</span> Trees merge. Conflicts surface. Obvious matches auto-resolve.</li>
        <li><span class="tick">◆</span> Open schema. GEDCOM in, GEDCOM out. Your data exits cleanly.</li>
      </ul>

      <div class="card signin">
        <div class="tabs" role="tablist">
          <button type="button" role="tab" class:active={mode==='signin'}
                  aria-selected={mode==='signin'} on:click={() => setMode('signin')}>
            Sign in
          </button>
          <button type="button" role="tab" class:active={mode==='signup'}
                  aria-selected={mode==='signup'} on:click={() => setMode('signup')}>
            I'm new
          </button>
        </div>
        <form on:submit|preventDefault={submit} class="stack">
          <div class="field">
            <label for="em">email</label>
            <input id="em" type="email" bind:value={email} required autocomplete="email"
                   placeholder="you@family.name" />
          </div>
          {#if error}<div class="pill danger">{error}</div>{/if}
          {#if info}<div class="pill ok">{info}</div>{/if}
          {#if invitedAuto && !error}<div class="pill ok">Welcome — signing you in…</div>{/if}
          <button type="submit" class="primary" disabled={loading}>
            {loading
              ? 'working…'
              : mode === 'signin'
                ? (hasCloud ? 'send sign-in link →' : 'sign in →')
                : 'create account →'}
          </button>
        </form>
        <p class="dim fine mono">
          {#if mode === 'signin'}
            already have an account? sign in here.<br/>
            new? <button type="button" class="linklike" on:click={() => setMode('signup')}>create an account →</button>
          {:else}
            {#if hasCloud}
              new account — we'll email you a one-time sign-in link. an admin approves you on first request; from then on, the link signs you in.
            {:else}
              <strong class="signal">Preview mode</strong> — accounts live in your browser. The first email you create becomes the admin for this device.
            {/if}
          {/if}
        </p>
      </div>
    </div>

    <aside class="art" aria-hidden="true">
      <div class="mark-wrap">
        <AnimatedTree generations={5} />
        <svg viewBox="0 0 400 420" class="tree-art" style="display:none">
          <defs>
            <linearGradient id="aga" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#c11c39"/>
              <stop offset="1" stop-color="#bf8a40"/>
            </linearGradient>
            <radialGradient id="agb" cx="50%" cy="0%" r="80%">
              <stop offset="0" stop-color="rgba(193,28,57,.10)"/>
              <stop offset="1" stop-color="transparent"/>
            </radialGradient>
          </defs>
          <rect width="400" height="420" fill="url(#agb)"/>
          <!-- 5-gen ancestor tree, INVERTED: YOU at top, parents fanning DOWN
               (ancestors below you, generation by generation) -->
          <g stroke="#b9a98c" stroke-width="1" fill="none" stroke-linecap="square" opacity=".95">
            <!-- you → parents -->
            <path d="M200 20 L200 80"/>
            <!-- parents → grandparents -->
            <path d="M200 80 L110 120 L110 160 M200 80 L290 120 L290 160"/>
            <!-- grandparents → great-grandparents -->
            <path d="M110 160 L60 200 L60 240 M110 160 L160 200 L160 240 M290 160 L240 200 L240 240 M290 160 L340 200 L340 240"/>
            <!-- great-grandparents → great-great-grandparents -->
            <path d="M60 240 L30 280 L30 310 M60 240 L90 280 L90 310 M160 240 L130 280 L130 310 M160 240 L190 280 L190 310 M240 240 L210 280 L210 310 M240 240 L270 280 L270 310 M340 240 L310 280 L310 310 M340 240 L370 280 L370 310"/>
          </g>
          <!-- you (root) at top -->
          <rect x="195" y="14" width="10" height="10" fill="url(#aga)"/>
          <!-- parents -->
          <rect x="195" y="74" width="10" height="10" fill="#bf8a40"/>
          <!-- grandparents -->
          <g fill="#d8ccb6">
            <rect x="105" y="154" width="10" height="10"/><rect x="285" y="154" width="10" height="10"/>
          </g>
          <!-- great-grandparents -->
          <g fill="#efe7d4">
            <rect x="55" y="234" width="10" height="10"/><rect x="155" y="234" width="10" height="10"/>
            <rect x="235" y="234" width="10" height="10"/><rect x="335" y="234" width="10" height="10"/>
          </g>
          <!-- great-great-grandparents -->
          <g fill="#fbf6ec" stroke="#d8ccb6" stroke-width=".5">
            <rect x="25" y="304" width="10" height="10"/><rect x="85" y="304" width="10" height="10"/>
            <rect x="125" y="304" width="10" height="10"/><rect x="185" y="304" width="10" height="10"/>
            <rect x="205" y="304" width="10" height="10"/><rect x="265" y="304" width="10" height="10"/>
            <rect x="305" y="304" width="10" height="10"/><rect x="365" y="304" width="10" height="10"/>
          </g>
        </svg>
        <div class="mark-overlay mono">
          <div>generations <span class="signal">5</span></div>
          <div>nodes <span class="signal">31</span></div>
          <div>realm <span class="dim">rlm-preview</span></div>
        </div>
      </div>
    </aside>
  </div>
{:else if $authState.status === 'pending'}
  <div class="splash single">
    <div class="hero enter" style="max-width:520px">
      <span class="chip signal">pending · admin approval</span>
      <h1>One step away.</h1>
      <p class="lede">
        Signed in as <span class="mono">{$authState.email}</span>. An admin will
        approve your account shortly. Your trees will appear here once they do.
      </p>
    </div>
  </div>
{:else}
  <slot />
{/if}

<style>
  .center { display: grid; place-items: center; height: 60vh; }

  .splash {
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    gap: 48px;
    max-width: 1220px;
    align-items: stretch;
    margin: 0 auto;
    padding: 48px 28px 72px;
    min-height: calc(100vh - var(--ls-nav-h));
  }
  .splash.single { grid-template-columns: 1fr; }

  .hero { max-width: 600px; }
  .meta { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px; }

  h1 {
    font-size: clamp(36px, 5.8vw, 62px);
    line-height: 1.02;
    letter-spacing: -0.035em;
  }
  h1 em {
    color: var(--ls-signal);
    font-style: italic;
    font-weight: 400;
  }
  .lede {
    font-family: var(--ls-font-display);
    font-size: var(--ls-step-2);
    line-height: 1.5;
    color: var(--ls-text-muted);
    margin: 22px 0 20px;
    max-width: 52ch;
  }
  .bullets {
    list-style: none; padding: 0; margin: 0 0 28px;
    display: grid; gap: 8px;
  }
  .bullets li { font-size: var(--ls-step-0); color: var(--ls-text); display: flex; gap: 10px; align-items: baseline; }
  .tick { color: var(--ls-accent); font-size: 10px; }

  .signin { max-width: 440px; }
  .tabs {
    display: flex; gap: 4px;
    margin: -4px -4px 12px -4px; padding: 4px;
    background: var(--co-paper-3); border-radius: var(--ls-radius);
  }
  .tabs button {
    flex: 1; padding: 8px 12px;
    background: transparent; border: 1px solid transparent;
    border-radius: calc(var(--ls-radius) - 2px);
    font: inherit; color: var(--co-text-muted);
    cursor: pointer; transition: all .14s var(--ls-ease);
  }
  .tabs button:hover { color: var(--co-text); }
  .tabs button.active {
    background: var(--co-paper-2); color: var(--co-text);
    border-color: var(--co-line); box-shadow: var(--ls-shadow-sm);
  }
  .linklike {
    background: none; border: none; padding: 0;
    color: var(--co-elephant); font: inherit;
    text-decoration: underline; cursor: pointer;
  }
  .linklike:hover { color: var(--co-elephant-2); }
  .pill.ok {
    background: var(--co-forest-tint); color: var(--co-forest-2);
    border: 1px solid var(--co-forest);
  }
  .fine { font-size: 11px; margin: 14px 0 0; line-height: 1.6; }

  .art { display: flex; align-items: center; justify-content: center; position: relative; }
  .mark-wrap { position: relative; width: 100%; max-width: 460px; }
  .tree-art {
    width: 100%; height: auto; display: block;
    filter: drop-shadow(0 30px 60px rgba(0,0,0,.5));
  }
  .mark-overlay {
    position: absolute; top: 18px; right: 12px;
    display: grid; gap: 4px;
    padding: 10px 14px;
    background: color-mix(in srgb, var(--ls-surface) 88%, transparent);
    border: 1px solid var(--ls-border);
    border-radius: 6px;
    font-size: 11px;
    color: var(--ls-text-muted);
    letter-spacing: 0;
  }
  .mark-overlay .signal { color: var(--ls-signal); font-weight: 600; }
  .mark-overlay .dim { color: var(--ls-text-dim); }

  @media (max-width: 900px) {
    .splash { grid-template-columns: 1fr; padding: 32px 20px 60px; gap: 28px; }
    .art { order: -1; }
    .tree-art { max-width: 320px; margin: 0 auto; }
  }
</style>
